/*
 *  Copyright (C) 2025 - SimpliCity Digital Inc - All Rights Reserved
 */

import React, { Fragment, useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, TextField } from "@mui/material";
import debounce from "lodash.debounce";
import LabelledField from "components/labelled-field";
import { SVGIcons } from "cms/icons/svg-icons";
import { ImageInPageEditorV2 } from "cms/images/image-inpage-editor-v2";
import { cloneObjectsArray } from "utils/array";
import { AddImageButton } from "./add-image-button";

const LinkDisplay = React.memo((props) => {
    
    const { t } = useTranslation();
    
    // Link selector props (from LinkDisplayWrapper)
    const link = props?.link;
    const onChange = props?.onChange;
    const onRemove = props?.onRemove;
    
    // Card props for updating card data
    const card = props?.card;
    const index = props?.index;
    const cardsList = props?.cardsList;
    const externalCardsListRef = props?.cardsListRef;
    const updateCards = props?.updateCards;

    const [title, setTitle] = useState(card?.title || link?.text);
    const [description, setDescription] = useState(card?.description);
    
    // Use refs to track if we're currently editing to prevent sync conflicts
    const isEditingTitleRef = useRef(false);
    const isEditingDescriptionRef = useRef(false);
    const lastCardTitleRef = useRef(card?.title);
    const lastCardDescriptionRef = useRef(card?.description);
    
    // Use refs to store latest cardsList and updateCards to prevent callback recreation
    const cardsListRef = useRef(cardsList);
    const updateCardsRef = useRef(updateCards);
    
    // Update refs when props change
    useEffect(() => {
        cardsListRef.current = cardsList;
        updateCardsRef.current = updateCards;
    }, [cardsList, updateCards]);

    const getCurrentCardsList = useCallback(() => {
        return externalCardsListRef?.current || cardsListRef.current;
    }, [externalCardsListRef]);

    // When link changes, populate title from link.text ONLY if card doesn't have a title
    // Title can be overwritten by user
    useEffect(() => {
        if (link && link?.guid && !card?.title) {
            const linkText = link?.text || "";
            if (linkText) {
                setTitle(linkText);
                lastCardTitleRef.current = linkText;
                // Update card with title from link (only if card doesn't have title)
                if (cardsList && updateCards && index !== undefined) {
                    const updatedCards = cloneObjectsArray(cardsList);
                    updatedCards[index] = {
                        ...(card || {}),
                        title: linkText,
                    };
                    updateCards?.(updatedCards);
                }
            }
        } else if (card?.title) {
            // Use card title if it exists (user has overwritten)
            if (!isEditingTitleRef.current) {
                setTitle(card.title);
                lastCardTitleRef.current = card.title;
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [link?.text, link?.guid, card?.title]);

    // Sync local state with card props when they change externally (only if not currently editing)
    useEffect(() => {
        if (!isEditingTitleRef.current && card?.title !== undefined && card.title !== title && card.title !== lastCardTitleRef.current) {
            setTitle(card.title);
            lastCardTitleRef.current = card.title;
        }
    }, [card?.title, title]);

    // Initialize description from card (never auto-populate from link - must be manual)
    useEffect(() => {
        if (!isEditingDescriptionRef.current && card?.description !== undefined && card.description !== description && card.description !== lastCardDescriptionRef.current) {
            setDescription(card?.description || "");
            lastCardDescriptionRef.current = card?.description;
        }
    }, [card?.description, description]);

    // Use refs to store latest title and description values for debounced handlers
    const titleRef = useRef(title);
    const descriptionRef = useRef(description);
    
    // Update refs when state changes
    useEffect(() => {
        titleRef.current = title;
    }, [title]);
    
    useEffect(() => {
        descriptionRef.current = description;
    }, [description]);

    // Debounced function to update card title
    const debouncedUpdateTitle = useRef(
        debounce((newTitle) => {
            isEditingTitleRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest description from ref to prevent losing pending updates
                updatedCards[index] = {
                    ...currentCard,
                    title: newTitle,
                    description: descriptionRef.current ?? currentCard.description,
                };
                lastCardTitleRef.current = newTitle;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    // Debounced function to update card description
    const debouncedUpdateDescription = useRef(
        debounce((newDescription) => {
            isEditingDescriptionRef.current = false;
            const currentCardsList = getCurrentCardsList();
            const currentUpdateCards = updateCardsRef.current;
            
            if (currentCardsList && currentUpdateCards && index !== undefined) {
                const updatedCards = cloneObjectsArray(currentCardsList);
                // Get current card from cardsList to ensure we have the latest data
                const currentCard = currentCardsList[index] || {};
                // Include latest title from ref to prevent losing pending updates
                updatedCards[index] = {
                    ...currentCard,
                    title: titleRef.current ?? currentCard.title,
                    description: newDescription,
                };
                lastCardDescriptionRef.current = newDescription;
                currentUpdateCards?.(updatedCards);
            }
        }, 500)
    ).current;

    const onChangeTitle = (event) => {
        const newTitle = event?.target?.value;
        isEditingTitleRef.current = true;
        setTitle(newTitle);
        titleRef.current = newTitle;
        // Debounce the card update
        debouncedUpdateTitle(newTitle);
    };

    const onChangeDescription = (event) => {
        const newDescription = event?.target?.value;
        isEditingDescriptionRef.current = true;
        setDescription(newDescription);
        descriptionRef.current = newDescription;
        // Debounce the card update
        debouncedUpdateDescription(newDescription);
    };

    // Get image GUID from banner_image object (banner_image.image contains the GUID)
    const getImageGuid = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's a string (old format), return it
        if (typeof bannerImageObj === "string") return bannerImageObj;
        // If it's an object, extract the image GUID
        return bannerImageObj?.image || null;
    }, []);
    
    // Get settings from banner_image object
    const getSettings = useCallback((bannerImageObj) => {
        if (!bannerImageObj) return null;
        // If it's an object with settings, return settings
        if (typeof bannerImageObj === "object" && bannerImageObj?.settings) {
            return bannerImageObj.settings;
        }
        return null;
    }, []);
    
    // Get banner_image data for ImageInPageEditorV2 - memoize to prevent re-renders
    const bannerImageObj = useMemo(() => card?.banner_image, [card?.banner_image]);
    const imageGuid = useMemo(() => getImageGuid(bannerImageObj), [bannerImageObj, getImageGuid]);
    const settings = useMemo(() => getSettings(bannerImageObj), [bannerImageObj, getSettings]);
    
    // ImageInPageEditorV2 handlers - memoize to prevent re-renders
    // Use refs instead of direct dependencies to prevent recreation when cardsList changes
    const onChangeImageWithSettings = useCallback((imageObj, newSettings) => {
        const imageGuidValue = imageObj?.guid || "";
        
        // Update card with banner_image object structure
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            const currentBannerImage = currentCard?.banner_image;
            
            // Preserve existing settings if they exist, otherwise use defaults
            const settingsToUse = newSettings || currentBannerImage?.settings || {
                format: "image.format.cropped-focus",
                options: {
                    method: "100Aspect",
                    position: "center center",
                    dimension: 400,
                    useCaption: false
                }
            };
            
            updatedCards[index] = {
                ...currentCard,
                banner_image: {
                    image: imageGuidValue,
                    settings: settingsToUse
                },
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    const onRemoveImage = useCallback(() => {
        // Remove banner_image from card
        const currentCardsList = getCurrentCardsList();
        const currentUpdateCards = updateCardsRef.current;
        
        if (currentCardsList && currentUpdateCards && index !== undefined) {
            const updatedCards = cloneObjectsArray(currentCardsList);
            // Get current card from cardsList to ensure we have the latest data
            const currentCard = currentCardsList[index] || {};
            updatedCards[index] = {
                ...currentCard,
                banner_image: null,
            };
            currentUpdateCards?.(updatedCards);
        }
    }, [index, getCurrentCardsList]); // Only depend on index, not cardsList or updateCards

    // Memoize ImageInPageEditorV2 props to prevent unnecessary re-renders
    const imageEditorProps = useMemo(() => ({
        image_guid: imageGuid,
        settings: settings,
        onChangeImageWithSettings: onChangeImageWithSettings,
        onRemoveImage: onRemoveImage,
        className: "link-display-image-inpage-editor-v2",
        AddButtonComponent: AddImageButton,
    }), [imageGuid, settings, onChangeImageWithSettings, onRemoveImage]);

    return (
        <Fragment>
            {link && link?.guid && (
                <div className='link-editor'>
                    <div className='link-editor-view'>
                        <div className='link-display'>
                            <div className='link-actions'>
                                <div className="card-link-display-link-selector-content-icon">
                                    <SVGIcons.common.Link />
                                </div>
                                <div className="card-link-display-link-selector-content-info">
                                    <div className="card-link-display-link-selector-content-info-title">
                                        {link?.text}
                                    </div>
                                    <div className="card-link-display-link-selector-content-info-url">
                                        {link?.url}
                                    </div>
                                </div>
                                <div className='card-link-display-link-selector-content-actions'>
                                    <div className='image-action-change'>
                                        <IconButton onClick={onChange}>
                                            <SVGIcons.common.Edit />
                                        </IconButton>
                                    </div>
                                    <div className='image-action-remove'>
                                        <IconButton onClick={onRemove}>
                                            <SVGIcons.common.Close />
                                        </IconButton>
                                    </div>
                                </div>
                            </div>
                            
                            <LabelledField label={t?.("commons.title")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='link_title'
                                    fullWidth={true}
                                    value={title || ""}
                                    onChange={onChangeTitle}
                                />
                            </LabelledField>
                            <LabelledField label={t?.("commons.description")} style={{ marginTop: 12 }}>
                                <TextField
                                    id='link_description'
                                    fullWidth={true}
                                    multiline={true}
                                    maxRows={4}
                                    value={description || ""}
                                    onChange={onChangeDescription}
                                />
                            </LabelledField>

                            {/* Image Editor */}
                            <LabelledField label="Image" style={{ marginTop: 12 }}>
                                <ImageInPageEditorV2 {...imageEditorProps} />
                            </LabelledField>
                        </div>
                    </div>
                </div>
            )}
        </Fragment>
    );
}, (prevProps, nextProps) => {
    // Custom comparison to prevent unnecessary re-renders
    const prevBannerImageGuid = prevProps.card?.banner_image?.image || 
        (typeof prevProps.card?.banner_image === "string" ? prevProps.card?.banner_image : null);
    const nextBannerImageGuid = nextProps.card?.banner_image?.image || 
        (typeof nextProps.card?.banner_image === "string" ? nextProps.card?.banner_image : null);
    
    return (
        prevProps.index === nextProps.index &&
        prevProps.link?.guid === nextProps.link?.guid &&
        prevBannerImageGuid === nextBannerImageGuid &&
        prevProps.card?.title === nextProps.card?.title &&
        prevProps.card?.description === nextProps.card?.description
    );
});

LinkDisplay.displayName = "LinkDisplay";
export { LinkDisplay };
